import React, { useEffect, useState } from 'react';
import { Box, Stack, Text, Card, Inline } from 'braid-design-system';
import { ReportProgressState } from '../../hooks/useReportProgress';

interface ReportProgressDisplayProps {
    progressState: ReportProgressState;
}

/**
 * Component to display report generation progress
 * Shows current step, percentage, and status messages
 */
export const ReportProgressDisplay: React.FC<ReportProgressDisplayProps> = ({ progressState }) => {
    const {
        isLoading,
        currentStatus,
        percentage,
        currentStep,
        totalSteps,
        error,
        isConnected
    } = progressState;

    const [showCompletionMessage, setShowCompletionMessage] = useState(false);

    // Show completion message when progress reaches 100%
    useEffect(() => {
        if (!isLoading && percentage === 100 && !error) {
            setShowCompletionMessage(true);
        }
    }, [isLoading, percentage, error]);

    const steps = [
        { number: 1, label: 'Executing SQLs' },
        { number: 2, label: 'Preparing Data' },
        { number: 3, label: 'Generating Report' },
        { number: 4, label: 'Downloading Report' },
        { number: 5, label: 'Finalize' },
    ];

    const currentStepLabel = currentStep > 0 && currentStep <= steps.length ? steps[currentStep - 1].label : '';

    // Ensure progress bar shows 100% when complete
    const displayPercentage = !isLoading && percentage < 100 ? 100 : percentage;

    return (
        <Box paddingBottom="medium">
            <Card>
                <Stack space="medium">
                    {/* Status Message */}
                    <Box>
                        <Stack space="small">
                            <Inline space="small" alignY="center">
                                {error ? (
                                    <Text weight="strong" tone="critical">
                                        ✗ Error: {error}
                                    </Text>
                                ) : !isConnected ? (
                                    <Text weight="strong" tone="info">
                                        ⟳ Connecting to progress service...
                                    </Text>
                                ) : isLoading ? (
                                    <Text weight="strong" tone="info">
                                        ⟳ {currentStepLabel} - In Process
                                    </Text>
                                ) : showCompletionMessage ? (
                                    <Text weight="strong" tone="positive">
                                        ✓ {currentStepLabel || currentStatus} - Completed!
                                    </Text>
                                ) : (
                                    <Text weight="strong" tone="positive">
                                        ✓ {currentStepLabel || currentStatus}
                                    </Text>
                                )}
                            </Inline>
                        </Stack>
                    </Box>

                    {/* Progress Bar */}
                    <Box>
                        <Box
                            style={{
                                width: '100%',
                                height: '12px',
                                backgroundColor: '#e0e0e0',
                                borderRadius: '6px',
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                style={{
                                    width: `${displayPercentage}%`,
                                    height: '100%',
                                    backgroundColor: error ? '#d32f2f' : '#1976d2',
                                    transition: 'width 0.5s ease-in-out',
                                    borderRadius: '6px',
                                    minWidth: displayPercentage === 100 ? '100%' : 'auto'
                                }}
                            />
                        </Box>
                        <Box paddingTop="xsmall">
                            <Text size="small" tone="secondary">
                                {displayPercentage}% Complete
                            </Text>
                        </Box>
                    </Box>

                    {/* Step Indicators */}
                    <Box>
                        <Stack space="small">
                            <Text weight="strong" size="small" tone="secondary">
                                Step {currentStep} of {totalSteps}
                            </Text>
                            <Inline space="small">
                                {steps.map((step) => (
                                    <Box
                                        key={step.number}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            backgroundColor:
                                                currentStep > step.number ? '#4caf50' :
                                                    currentStep === step.number ? '#1976d2' :
                                                        '#e0e0e0',
                                            color: currentStep >= step.number ? 'white' : '#666',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            cursor: 'default'
                                        }}
                                        title={step.label}
                                    >
                                        {currentStep > step.number ? '✓' : step.number}
                                    </Box>
                                ))}
                            </Inline>
                        </Stack>
                    </Box>
                </Stack>
            </Card>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </Box>
    );
};

export default ReportProgressDisplay;
